import * as RMenu from '@radix-ui/react-dropdown-menu'
import { Fragment, type ReactNode } from 'react'

import { cn } from '../cn'

// DropdownMenu — a list of actions (row menu, user menu)
// (design/components/overlays.html §5.2). Built on Radix DropdownMenu (↑↓
// navigation, Enter, Esc, return-focus). Destructive items are visually
// distinct (red) and a separator is inserted before the first one.

export interface MenuItem {
  label: string
  icon?: ReactNode
  danger?: boolean
  disabled?: boolean
  onSelect: () => void
}

export interface DropdownMenuProps {
  trigger: ReactNode
  items: MenuItem[]
  align?: 'start' | 'end'
}

/** Action menu on Radix DropdownMenu (↑↓, Enter, Esc, return-focus). The
 *  `trigger` is rendered asChild, so pass a real button (forwardRef).
 *  `danger` items go red and get one separator before the first of them. */
export function DropdownMenu({ trigger, items, align = 'end' }: DropdownMenuProps) {
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild>{trigger}</RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content
          align={align}
          sideOffset={6}
          className="z-50 w-[198px] rounded-lg border border-line bg-surface p-[5px] shadow-card focus:outline-none"
        >
          {items.map((it, i) => {
            // One separator before the first danger item (never before a leading
            // one), regardless of whether danger items are contiguous.
            const firstDanger = items.findIndex((x) => x.danger)
            const sepBefore = firstDanger > 0 && i === firstDanger
            return (
              <Fragment key={i}>
                {sepBefore && <RMenu.Separator className="mx-1 my-[5px] h-px bg-line" />}
                <RMenu.Item
                  disabled={it.disabled ?? false}
                  onSelect={it.onSelect}
                  className={cn(
                    'flex cursor-pointer items-center gap-[9px] rounded-sm px-2.5 py-2 text-[12px] outline-none',
                    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 [&_svg]:size-[14px]',
                    it.danger
                      ? 'text-sens data-[highlighted]:bg-sens-bg [&_svg]:text-sens'
                      : 'text-text data-[highlighted]:bg-hover [&_svg]:text-text-3',
                  )}
                >
                  {it.icon}
                  {it.label}
                </RMenu.Item>
              </Fragment>
            )
          })}
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  )
}
